import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import { ConfilexMark, ConfilexWordmark } from "@/components/ConfilexLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Le backend renvoie toujours 200 — sauf si email format invalide
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error("Adresse email invalide.");
      } else {
        toast.error(detail || "Une erreur est survenue. Réessayez dans un instant.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B3A2A] text-white flex items-center justify-center p-6 grain relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src="/art/sorola/promenade-1.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3A2A]/60 to-[#0B3A2A]"></div>
      </div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10" data-testid="back-home">
          <ConfilexMark size={40} theme="light" />
          <ConfilexWordmark size={22} theme="light" />
        </Link>

        <div className="glass-dark rounded-2xl p-8 fade-up" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <h1 className="font-serif text-3xl mb-2">Mot de passe oublié</h1>
              <p className="text-white/60 text-sm mb-8">
                Indiquez l'adresse email associée à votre compte CONFILEX. Nous vous enverrons un lien sécurisé pour définir un nouveau mot de passe.
              </p>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-white/80 mb-2 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email"
                    className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]"
                    placeholder="vous@exemple.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit"
                  className="w-full h-11 rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold">
                  {loading ? "Envoi en cours…" : "Envoyer le lien de réinitialisation"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center mb-4">
                <MailCheck className="w-7 h-7 text-[#C9A961]" />
              </div>
              <h1 className="font-serif text-2xl mb-2">Vérifiez vos emails</h1>
              <p className="text-white/70 text-sm leading-relaxed">
                Si l'adresse <span className="text-[#C9A961]">{email}</span> est associée à un compte CONFILEX, un email contenant le lien de réinitialisation vient d'être envoyé.
              </p>
              <p className="text-white/50 text-xs mt-4">
                Pensez à vérifier vos spams. Le lien est valable 60 minutes.
              </p>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <Link to="/login" className="text-[#C9A961] hover:underline text-sm inline-flex items-center gap-1" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
